import { useEffect, useRef, useState } from "react";

import { Ellipses, Settings, SharePeople, Withdraw } from "@assets";
import { ShareGoalModal } from "@components/Goals/ShareGoalModal";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body, BodySmall } from "@components/Typography";
import { Flow, Routes } from "@ctypes";
import { useErrorBanner, useGoalsContext, useModal, useNavigateToStep, useUser } from "@hooks";
import { i18n, Translations } from "@i18n";
import { useStyles } from "./styles";
import { MenuItemProps } from "./types";

export const MoreMenu = () => {
  const styles = useStyles();

  const { hasBankAccount } = useUser();

  const navigate = useNavigateToStep();
  const { show } = useModal();
  const { showErrorBanner } = useErrorBanner();

  const { beneficiaryId, goalId } = useGoalsContext();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLDivElement | null>(null);

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      buttonRef.current &&
      !buttonRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleShareGoal = () => {
    setIsOpen(false);

    if (!goalId) return showErrorBanner();

    show(<ShareGoalModal goalId={goalId} />);
  };

  const handleWithdrawCTA = () => {
    if (hasBankAccount) {
      navigate(Routes.Withdraw, { state: { goalId } });
    } else {
      navigate(Routes.BankLinking, { state: { flow: Flow.B2B } });
    }
  };

  return (
    <div style={styles.container}>
      <div ref={buttonRef} onClick={toggleDropdown}>
        <Ellipses />
      </div>

      {isOpen && (
        <div ref={dropdownRef} style={styles.inner}>
          <Spacing size={SpacingSizes.M} />
          <MenuItem
            title={i18n.t(Translations.GoalBreakdownRouteEditGoalTitle)}
            subtitle={i18n.t(Translations.GoalBreakdownRouteEditGoalSubtitle)}
            icon={<Settings />}
            onClick={() => navigate(Routes.EditGoal, { state: { beneficiaryId, goalId } })}
          />
          <MenuItem
            title={i18n.t(Translations.GoalBreakdownRouteWithdrawTitle)}
            subtitle={i18n.t(Translations.GoalBreakdownRouteWithdrawSubtitle)}
            icon={<Withdraw />}
            onClick={handleWithdrawCTA}
          />
          <MenuItem
            title={i18n.t(Translations.GoalBreakdownRouteShareGoalTitle)}
            subtitle={i18n.t(Translations.GoalBreakdownRouteShareGoalSubtitle)}
            icon={<SharePeople />}
            onClick={handleShareGoal}
          />
        </div>
      )}
    </div>
  );
};

const MenuItem = ({ title, subtitle, icon, onClick }: MenuItemProps) => {
  const styles = useStyles();

  return (
    <div style={styles.item} onClick={onClick}>
      {icon}
      <div style={styles.text}>
        <Body>{title}</Body>
        {subtitle ? <BodySmall>{subtitle}</BodySmall> : null}
      </div>
    </div>
  );
};
